import { message } from "ant-design-vue";
import { type AxiosResponse, type AxiosInstance } from "axios";
import { createBaseAxiosInstance, aesDecrypt, aesEncrypt, objExt } from "@xmiles/holopix-helper";
import { useUserStore } from "@/store/user";
import type { GetRequestOption, PostRequestOption, PutRequestOption, DeleteRequestOption, PoseRequestOption, ServerResponse } from "@/types/request";
import { responseCodeHandle } from "./responseCodeHandle";
import { checkDateChange } from "@/utils/dateChecker";
import { BOKE_ERROR_CODE_MAP } from "@/hooks/useBoke";
import { useAccessDeniedStore } from "@/store/accessDenied";
import { getToken, logTokenExpiry, TokenExpiryReason, logToken, tryAdoptSharedCredential, extractTokenFromRequestConfig } from "@/utils/tokenManager";
/**
 * 主服务axios实例 - 基于holopix-helper的基础实例
 * 注意：holopix-helper 在未传 timeout 时默认 30000ms；此处必须显式设置。
 * 表达式 = 30 分钟（注释曾误写为 3 分钟）。
 */
const AXIOS_DEFAULT_TIMEOUT_MS = 60 * 10 * 1000 * 3;

const service: AxiosInstance = createBaseAxiosInstance({
  baseURL: import.meta.env.VITE_SERVICE_URL,
  timeout: AXIOS_DEFAULT_TIMEOUT_MS,
});
// 双保险：防止部分打包场景下 factory 未透传 timeout 而回落到 helper 默认 30s
service.defaults.timeout = AXIOS_DEFAULT_TIMEOUT_MS;

/** chat/completions（智能强化）单请求超时：LLM 推理常超过 helper 默认 30s */
const CHAT_COMPLETIONS_TIMEOUT_MS = 3 * 60 * 1000;

const useAes = ["production", "gray"].includes(import.meta.env.VITE_ENV);
/**
 * 业务请求拦截器 - 在基础拦截器之后执行
 */
service.interceptors.request.use((config) => {
  // 在每次请求前检查是否跨天
  checkDateChange();

  // 使用集中式 token 读取（内存→localStorage fallback）
  const token = getToken();
  if (token) {
    config.headers.token = token;
  }

  // 处理数据加密
  if (useAes) {
    if (["post", "put"].includes(config!.method!.toLowerCase())) {
      const value = aesEncrypt(objExt.stringify(config.data));
      config.data = value;
    }
  } else {
    // 加密后门 添加后则不使用加密
    config.headers.xmReqRaw = 1;
    config.headers.xmRespRaw = 1;
  }

  return config;
});

/**
 * 业务响应拦截器 - 在基础拦截器之后执行
 */
service.interceptors.response.use(
  async (response: AxiosResponse): Promise<AxiosResponse> => {
    // 解密响应数据
    if (useAes && typeof response.data === "string") {
      const result = aesDecrypt(response.data);
      response.data = objExt.parse(result, {});
    }

    // 处理业务响应码（带上请求 config，供失效处理取回该请求实际携带的 token 做比对换新）
    if (response.data && response.data.code >= 0) {
      const responseCodeHandleResult = responseCodeHandle(response.data, { headers: response.config?.headers });

      if (typeof responseCodeHandleResult !== "undefined") {
        return responseCodeHandleResult;
      }

      if (response.data.code === 0) {
        return response.data;
      }
    }

    return Promise.reject(response.data) as any;
  },
  (error) => {
    // HTTP 状态码错误处理（网络层，非业务层）
    const httpStatus = error?.response?.status;
    if (httpStatus === 401) {
      // HTTP 401 = 未授权 / token 过期
      const userStore = useUserStore();
      logTokenExpiry(TokenExpiryReason.API_401, {
        httpStatus,
        url: error?.config?.url,
        method: error?.config?.method,
        tokenPrefix: (getToken() || "").substring(0, 8),
      });

      if (userStore.isLogin) {
        // 被动 401 先比对共享凭证（用失败请求实际携带的 token，防止 storage 同步竞态误判）：
        // 其他标签页/端刚写入新登录态时静默换新并刷新本页，
        // 不走登出清理（清共享存储/Cookie/广播会把刚登录的标签页连坐踢下线）
        const failedToken = extractTokenFromRequestConfig(error?.config);
        if (tryAdoptSharedCredential(failedToken) === "adopted") {
          logToken("HTTP 401 时共享凭证已更新，静默换新并刷新本页", {
            url: error?.config?.url,
            trigger: "passive_adopt_shared_credential",
            failedTokenPrefix: failedToken.substring(0, 8),
            tokenPrefix: (getToken() || "").substring(0, 8),
          });
          window.location.reload();
          return Promise.reject(error.response || error);
        }

        userStore.logout({ useLogout: false, reason: TokenExpiryReason.API_401, redirect: true });
      }
    }
    return Promise.reject(error.response || error);
  }
);

/**
 * 数据包装处理
 */
const wrapRequestData = (options: any) => {
  if (["post", "POST", "put", "PUT", "delete", "DELETE"].includes(options.method)) {
    if (typeof options.data === "object" && options.data !== null) {
      options.data = {
        data: options.data
      };
    }
  }
  return options;
};
/**
 * 错误码是否包含在BOKE_ERROR_CODE_MAP中
 */
const isIpErrorCode = (errorCode: number) => {
  // err.code 可能为 undefined（如 Network Error / 断网等无响应错误），直接 toString 会抛 TypeError
  if (errorCode === null || errorCode === undefined) {
    return false;
  }
  return Object.keys(BOKE_ERROR_CODE_MAP).includes(errorCode.toString());
};

/**
 * 显示访问受限制弹窗
 */
const showAccessDeniedModal = (errorCode: number) => {
  const accessDeniedStore = useAccessDeniedStore();
  console.log("显示访问受限制弹窗执行显示方法");
  // 如果已经显示过，则不重复显示
  if (accessDeniedStore.visible) {
    console.log("已经显示过，不重复显示");
    return;
  }
  // 提示文案
  const errorMessage = BOKE_ERROR_CODE_MAP[errorCode as keyof typeof BOKE_ERROR_CODE_MAP] || "无权限访问";
  accessDeniedStore.showAccessDenied(errorMessage);
};

/**
 * 错误处理
 */
const handleRequestError = (err: any, options: any) => {
  // 如果是取消错误，静默处理，不显示 toast
  if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
    return;
  }

  // 90711 已在 responseCodeHandle 中弹 Modal，避免重复 toast
  if (err?.code === 90711) {
    return;
  }

  // 90712 已在 responseCodeHandle 中弹 Modal，避免重复 toast
  if (err?.code === 90712) {
    return;
  }
  
  if (err && !options.notToast && err.code !== 90000) {
    if (isIpErrorCode(err.code)) {
      showAccessDeniedModal(err.code);
    } else if (typeof err === "string") {
      message.error(err);
    } else if (typeof err.msg === "string") {
      message.error(err.msg);
    } else if (typeof err.message === "string") {
      message.error(err.message);
    }
  }
};

/**
 * 统一请求函数 - 基于业务axios实例
 */
function request<T = any>(
  options: GetRequestOption | PostRequestOption | PutRequestOption | DeleteRequestOption | PoseRequestOption
): Promise<ServerResponse<T>> {
  // 数据包装处理
  const wrappedOptions = wrapRequestData({ ...options });

  // 确保 signal 参数被传递
  if ((options as any).signal) {
    (wrappedOptions as any).signal = (options as any).signal;
  }

  // 单请求 timeout 显式透传，避免被实例默认/打包差异吞掉
  if (typeof options.timeout === 'number') {
    (wrappedOptions as any).timeout = options.timeout;
  }

  return service(wrappedOptions.api, wrappedOptions)
    .then((res) => res)
    .catch((err) => {
      handleRequestError(err, options);
      return Promise.reject(err);
    }) as Promise<ServerResponse<T>>;
}

export { request, service, CHAT_COMPLETIONS_TIMEOUT_MS };
